import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Star, Eye, EyeOff, Trash2, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReviewType } from "./types";
import { useState } from "react";

interface ReviewTableProps {
  reviews: ReviewType[];
  selectedReviews: string[];
  onSelectReview: (reviewId: string, checked: boolean) => void;
  onToggleVisibility: (reviewId: string) => void;
  onToggleFeatured: (reviewId: string) => void;
  onDeleteRequest: (reviewId: string) => void;
}

export const ReviewTable = ({
  reviews,
  selectedReviews,
  onSelectReview,
  onToggleVisibility,
  onToggleFeatured,
  onDeleteRequest
}: ReviewTableProps) => {
  const [loadingFeature, setLoadingFeature] = useState<string | null>(null);
  const [loadingVisibility, setLoadingVisibility] = useState<string | null>(null);
  
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        className={`inline-block h-4 w-4 ${i < rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} 
      />
    ));
  };

  if (reviews.length === 0) {
    return (
      <div className="text-center py-8 border rounded-lg bg-muted/5">
        <p className="text-muted-foreground">No reviews found</p>
      </div>
    );
  }

  const handleToggleVisibility = async (review: ReviewType) => {
    try {
      console.log(`[ToggleVisibility] Starting - Review ID: ${review.id}, Current visibility: ${review.is_public}`);
      setLoadingVisibility(review.id);
      
      onToggleVisibility(review.id);
    } catch (error) {
      console.error(`[ToggleVisibility] Error - Review ID: ${review.id}:`, error);
    } finally {
      setTimeout(() => {
        setLoadingVisibility(null);
      }, 500);
    }
  };

  const handleToggleFeatured = async (review: ReviewType) => {
    try {
      console.log(`[ToggleFeatured] Starting - Review ID: ${review.id}, Current featured status: ${review.is_featured}`);
      setLoadingFeature(review.id);
      
      onToggleFeatured(review.id);
    } catch (error) {
      console.error(`[ToggleFeatured] Error - Review ID: ${review.id}:`, error);
    } finally {
      setTimeout(() => {
        setLoadingFeature(null);
      }, 500);
    }
  };

  const handleDeleteRequest = (reviewId: string) => {
    console.log(`[DeleteRequest] Requesting deletion - Review ID: ${reviewId}`);
    onDeleteRequest(reviewId);
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Student</TableHead>
          <TableHead>Rating</TableHead>
          <TableHead className="w-[400px]">Review</TableHead>
          <TableHead>Date</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {reviews.map((review) => (
          <TableRow 
            key={review.id}
            className={`${!review.is_public ? 'bg-muted/50' : ''} ${review.is_featured ? 'bg-yellow-50 dark:bg-yellow-950/20' : ''}`}
          >
            <TableCell className="font-medium">
              {review.profiles?.full_name || 'Anonymous'}
            </TableCell>
            <TableCell>
              <div className="flex gap-0.5">
                {renderStars(review.rating)}
              </div>
            </TableCell>
            <TableCell className="max-w-[400px]">
              <p className="text-sm text-muted-foreground line-clamp-2">
                {review.review || 'No written review'}
              </p>
            </TableCell>
            <TableCell>
              {new Date(review.created_at).toLocaleDateString()}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-2">
                <Button 
                  variant={review.is_featured ? "default" : "ghost"}
                  size="icon"
                  onClick={() => handleToggleFeatured(review)}
                  title={review.is_featured ? "Unfeature review" : "Feature review"}
                  disabled={loadingFeature === review.id}
                  className={review.is_featured ? "bg-yellow-500 hover:bg-yellow-600" : ""}
                >
                  {loadingFeature === review.id ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : review.is_featured ? (
                    <Award className="h-4 w-4 fill-current" />
                  ) : (
                    <Star className="h-4 w-4" />
                  )}
                </Button>
                
                <Button 
                  variant={!review.is_public ? "default" : "ghost"}
                  size="icon"
                  onClick={() => handleToggleVisibility(review)}
                  title={review.is_public ? "Hide review" : "Show review"}
                  disabled={loadingVisibility === review.id}
                  className={!review.is_public ? "bg-slate-500 hover:bg-slate-600" : ""}
                >
                  {loadingVisibility === review.id ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : review.is_public ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
                
                <Button 
                  variant="ghost"
                  size="icon"
                  className="text-destructive hover:bg-destructive/10"
                  onClick={() => handleDeleteRequest(review.id)}
                  title="Delete review"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
